import React, { useState, useRef } from "react";
import { useApp } from "@/context/AppContext";
import { Plus, X, Pencil, GripVertical } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export function DayPills() {
  const { state, activeDay, setActiveDay, addDay, editDay, deleteDay, reorderDays } = useApp();

  const [addOpen, setAddOpen] = useState(false);
  const [newDayName, setNewDayName] = useState("");
  const [newDayShort, setNewDayShort] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editName, setEditName] = useState("");
  const [editShort, setEditShort] = useState("");

  const [confirmDelete, setConfirmDelete] = useState(null);

  // Drag & drop state
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragCounter = useRef(0);

  if (!state) return null;

  // ─── Add day ────────────────────────────────────────────────────────────────
  const handleAdd = () => {
    const name = newDayName.trim();
    const short = newDayShort.trim() || `D${state.routine.length + 1}`;
    if (!name) return;
    addDay(name, short);
    setAddOpen(false);
    setNewDayName("");
    setNewDayShort("");
    toast.success(`"${name}" añadido`);
  };

  // ─── Edit day ────────────────────────────────────────────────────────────────
  const openEdit = (e, idx) => {
    e.stopPropagation();
    setEditingIdx(idx);
    setEditName(state.routine[idx].name);
    setEditShort(state.routine[idx].short);
    setEditOpen(true);
  };

  const handleEdit = () => {
    const name = editName.trim();
    const short = editShort.trim();
    if (!name || editingIdx === null) return;
    editDay(editingIdx, name, short || state.routine[editingIdx].short);
    setEditOpen(false);
    toast.success("Día actualizado");
  };

  // ─── Delete day ──────────────────────────────────────────────────────────────
  const handleDelete = () => {
    if (confirmDelete === null) return;
    deleteDay(confirmDelete);
    setConfirmDelete(null);
    toast.success("Día eliminado");
  };

  // ─── Drag & drop ─────────────────────────────────────────────────────────────
  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== idx) {
      reorderDays(dragIdx, idx);
      toast.success("Días reordenados");
    }
    setDragIdx(null);
    setDragOverIdx(null);
    dragCounter.current = 0;
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
    dragCounter.current = 0;
  };

  return (
    <>
      <div className="flex gap-1.5 mb-5 overflow-x-auto fp-scrollbar pb-1 -mx-1 px-1 items-center">
        {state.routine.map((d, i) => {
          const active = i === activeDay;
          const isDragging = dragIdx === i;
          const isDragOver = dragOverIdx === i && dragIdx !== i;

          return (
            <motion.div
              key={i}
              draggable
              onDragStart={(e) => handleDragStart(e, i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={(e) => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
              whileTap={{ scale: 0.95 }}
              style={active ? {
                background: "hsl(var(--primary))",
                boxShadow: "0 0 20px hsl(var(--primary) / 0.30), 0 2px 8px rgba(0,0,0,0.4)",
              } : {
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
              className={`
                relative flex-shrink-0 rounded-2xl text-[11px] font-bold tracking-[0.06em] uppercase
                min-h-[40px] flex items-center justify-center gap-0.5
                transition-all duration-200 select-none
                ${active
                  ? "text-white"
                  : "text-white/35 hover:text-white/60"
                }
                ${isDragging ? "opacity-40 scale-95" : ""}
                ${isDragOver ? "ring-2 ring-primary/60 ring-offset-1 ring-offset-background" : ""}
              `}
            >
              {/* Drag handle */}
              <span
                className={`pl-2 cursor-grab active:cursor-grabbing flex-shrink-0 transition-colors ${active ? "text-primary-foreground/40 hover:text-primary-foreground/70" : "text-muted-foreground/30 hover:text-muted-foreground/60"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="h-3 w-3" />
              </span>

              {/* Day label (clickable) */}
              <button
                onClick={() => setActiveDay(i)}
                onContextMenu={(e) => { e.preventDefault(); setConfirmDelete(i); }}
                className="pr-1"
              >
                {d.short}
              </button>

              {/* Active day actions */}
              {active && (
                <div className="flex items-center gap-0.5 pr-1.5">
                  <button
                    onClick={(e) => openEdit(e, i)}
                    className="h-5 w-5 rounded-full flex items-center justify-center text-primary-foreground/60 hover:text-primary-foreground hover:bg-white/15 transition-colors"
                  >
                    <Pencil className="h-2.5 w-2.5" />
                  </button>
                  {state.routine.length > 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(i); }}
                      className="h-5 w-5 rounded-full flex items-center justify-center text-primary-foreground/50 hover:text-red-200 hover:bg-white/15 transition-colors"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          );
        })}

        {/* Add day button */}
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => setAddOpen(true)}
          aria-label="Añadir día"
          className="flex-shrink-0 h-[44px] w-[44px] rounded-2xl flex items-center justify-center border border-dashed border-border/60 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors duration-200"
        >
          <Plus className="h-4 w-4" />
        </motion.button>
      </div>

      {/* Add day dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader className="text-left">
              <DialogTitle className="text-foreground text-lg font-bold tracking-tight">Añadir día</DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">Crea un nuevo día de entrenamiento.</DialogDescription>
            </DialogHeader>
          </div>
          <div className="p-6 pt-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">Nombre</Label>
              <Input value={newDayName} onChange={(e) => setNewDayName(e.target.value)}
                placeholder="Ej: Día 4 · Full Body"
                className="h-11 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40 placeholder:text-muted-foreground/40"
                autoFocus onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">Etiqueta corta</Label>
              <Input value={newDayShort} onChange={(e) => setNewDayShort(e.target.value)}
                placeholder={`D${state.routine.length + 1}`} maxLength={4}
                className="h-11 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40 placeholder:text-muted-foreground/40" />
            </div>
            <Button onClick={handleAdd} disabled={!newDayName.trim()}
              className="w-full h-11 mt-1 rounded-2xl bg-primary text-primary-foreground font-bold text-sm fp-glow hover:bg-primary/90 disabled:opacity-40 disabled:shadow-none">
              Añadir día
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit day dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader className="text-left">
              <DialogTitle className="text-foreground text-lg font-bold tracking-tight">Editar día</DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">Cambia el nombre o la etiqueta.</DialogDescription>
            </DialogHeader>
          </div>
          <div className="p-6 pt-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">Nombre</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                className="h-11 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40"
                autoFocus onKeyDown={(e) => e.key === "Enter" && handleEdit()} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">Etiqueta corta</Label>
              <Input value={editShort} onChange={(e) => setEditShort(e.target.value)}
                maxLength={4}
                className="h-11 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40" />
            </div>
            <Button onClick={handleEdit} disabled={!editName.trim()}
              className="w-full h-11 mt-1 rounded-2xl bg-primary text-primary-foreground font-bold text-sm fp-glow hover:bg-primary/90 disabled:opacity-40 disabled:shadow-none">
              Guardar cambios
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm delete dialog */}
      <Dialog open={confirmDelete !== null} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Eliminar día</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Eliminar &quot;{confirmDelete !== null ? state.routine[confirmDelete]?.name : ""}&quot;? Se borrarán todos sus ejercicios y progreso.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}
              className="flex-1 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60">Cancelar</Button>
            <Button onClick={handleDelete}
              className="flex-1 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
